import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { humanizeAuthErrorCode, looksLikeErrorCode } from '../utils/api-error.util';
import { MobileAlertService } from './mobile-alert.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly translate = inject(TranslateService);

  constructor(private readonly mobileAlert: MobileAlertService) { }

  private static readonly CRITICAL_FALLBACKS: Record<string, { fr: string; en: string }> = {
    success: { fr: 'Succès', en: 'Success' },
    error: { fr: 'Erreur', en: 'Error' },
    info: { fr: 'Information', en: 'Information' },
    warning: { fr: 'Attention', en: 'Warning' },
    ok: { fr: 'OK', en: 'OK' },
    cancel: { fr: 'Annuler', en: 'Cancel' },
    loading: { fr: 'Chargement...', en: 'Loading...' },
    genericError: { fr: 'Une erreur est survenue.', en: 'An error occurred.' },
    yesDelete: { fr: 'Oui, supprimer', en: 'Yes, delete' },
    yesAction: { fr: 'Oui, {{action}}', en: 'Yes, {{action}}' }
  };

  private tCommon(key: string, params?: Record<string, unknown>): string {
    const fullKey = `dashboard.common.${key}`;
    const translated = this.translate.instant(fullKey, params);
    if (translated && translated !== fullKey) {
      return translated;
    }

    const rootKey = `common.${key}`;
    const rootTranslated = this.translate.instant(rootKey, params);
    if (rootTranslated && rootTranslated !== rootKey) {
      return rootTranslated;
    }

    return this.criticalFallback(key, params);
  }

  private criticalFallback(key: string, params?: Record<string, unknown>): string {
    const lang = (this.translate.currentLang || this.translate.defaultLang || 'fr').startsWith('en')
      ? 'en'
      : 'fr';
    let base = NotificationService.CRITICAL_FALLBACKS[key]?.[lang] ?? key;
    if (params) {
      for (const [paramKey, paramValue] of Object.entries(params)) {
        base = base.replace(new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g'), String(paramValue ?? ''));
      }
    }
    return base;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /** Évite d'afficher une clé i18n (`a.b.c`) ou un code API brut dans SweetAlert. */
  private sanitizeAlertText(value: string | undefined, fallback: string): string {
    if (!value || !String(value).trim()) {
      return fallback;
    }
    const text = String(value).trim();
    // Clé i18n non résolue → tenter la traduction, sinon fallback lisible
    if (/^[a-zA-Z][\w-]*(\.[a-zA-Z][\w-]*){2,}$/.test(text) && !text.includes(' ')) {
      const translated = this.translate.instant(text);
      if (translated && translated !== text) {
        return translated;
      }
      return fallback;
    }
    if (looksLikeErrorCode(text)) {
      return humanizeAuthErrorCode(text) || fallback;
    }
    return text;
  }

  private useMobileAlert(): boolean {
    return this.mobileAlert.isMobileViewport();
  }

  async confirmDelete(title: string, text: string): Promise<boolean> {
    const confirmText = this.tCommon('yesDelete');
    const cancelText = this.tCommon('cancel');

    if (this.useMobileAlert()) {
      return this.mobileAlert.confirm({
        title,
        text,
        tone: 'warning',
        confirmText,
        cancelText,
        confirmTone: 'danger'
      });
    }

    const result = await Swal.fire({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      reverseButtons: true
    });
    return result.isConfirmed;
  }

  async confirmAction(title: string, text: string, action: string): Promise<boolean> {
    const confirmText = this.tCommon('yesAction', { action });
    const cancelText = this.tCommon('cancel');

    if (this.useMobileAlert()) {
      return this.mobileAlert.confirm({
        title,
        text,
        tone: 'question',
        confirmText,
        cancelText,
        confirmTone: 'success'
      });
    }

    const result = await Swal.fire({
      title,
      text,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      reverseButtons: true
    });
    return result.isConfirmed;
  }

  success(title: string, text?: string) {
    const safeTitle = this.sanitizeAlertText(title, this.tCommon('success'));
    const safeText = text !== undefined ? this.sanitizeAlertText(text, '') : undefined;
    const okText = this.tCommon('ok');

    if (this.useMobileAlert()) {
      void this.mobileAlert.alert({
        title: safeTitle,
        text: safeText,
        tone: 'success',
        confirmText: okText
      });
      return;
    }

    Swal.fire({
      title: safeTitle,
      text: safeText,
      icon: 'success',
      confirmButtonColor: '#28a745',
      confirmButtonText: okText,
      timer: 3000,
      timerProgressBar: true
    });
  }

  error(title: string, text?: string) {
    const safeTitle = this.sanitizeAlertText(title, this.tCommon('error'));
    const safeText = text !== undefined ? this.sanitizeAlertText(text, this.tCommon('genericError')) : undefined;
    const hasNewlines = !!safeText && safeText.includes('\n');
    const okText = this.tCommon('ok');

    if (this.useMobileAlert()) {
      void this.mobileAlert.alert({
        title: safeTitle,
        text: safeText,
        tone: 'error',
        confirmText: okText
      });
      return;
    }

    Swal.fire({
      title: safeTitle,
      ...(hasNewlines
        ? {
            html: `<pre style="text-align:left;white-space:pre-wrap;margin:0;">${this.escapeHtml(safeText!)}</pre>`
          }
        : { text: safeText }),
      icon: 'error',
      confirmButtonColor: '#dc3545',
      confirmButtonText: okText
    });
  }

  info(title: string, text?: string) {
    const safeTitle = this.sanitizeAlertText(title, this.tCommon('info'));
    const safeText = text !== undefined ? this.sanitizeAlertText(text, '') : undefined;
    const hasNewlines = !!safeText && safeText.includes('\n');
    const okText = this.tCommon('ok');

    if (this.useMobileAlert()) {
      void this.mobileAlert.alert({
        title: safeTitle,
        text: safeText,
        tone: 'info',
        confirmText: okText
      });
      return;
    }

    Swal.fire({
      title: safeTitle,
      ...(hasNewlines
        ? {
            html: `<pre style="text-align:left;white-space:pre-wrap;margin:0;">${this.escapeHtml(safeText!)}</pre>`
          }
        : { text: safeText }),
      icon: 'info',
      confirmButtonColor: '#17a2b8',
      confirmButtonText: okText
    });
  }

  warning(title: string, text?: string) {
    const safeTitle = this.sanitizeAlertText(title, this.tCommon('warning'));
    const safeText = text !== undefined ? this.sanitizeAlertText(text, '') : undefined;
    const hasNewlines = !!safeText && safeText.includes('\n');
    const okText = this.tCommon('ok');

    if (this.useMobileAlert()) {
      void this.mobileAlert.alert({
        title: safeTitle,
        text: safeText,
        tone: 'warning',
        confirmText: okText
      });
      return;
    }

    Swal.fire({
      title: safeTitle,
      ...(hasNewlines
        ? {
            html: `<pre style="text-align:left;white-space:pre-wrap;margin:0;">${this.escapeHtml(safeText!)}</pre>`
          }
        : { text: safeText }),
      icon: 'warning',
      confirmButtonColor: '#ffc107',
      confirmButtonText: okText
    });
  }

  showLoading(title?: string) {
    const loadingTitle = title ?? this.tCommon('loading');
    if (this.useMobileAlert()) {
      this.mobileAlert.showLoading(loadingTitle);
      return;
    }
    Swal.fire({
      title: loadingTitle,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
  }

  closeLoading() {
    if (this.useMobileAlert() && this.mobileAlert.snapshot?.loading) {
      this.mobileAlert.close();
      return;
    }
    Swal.close();
  }

  dismiss(): void {
    if (this.useMobileAlert() && this.mobileAlert.snapshot?.open) {
      this.mobileAlert.close();
      return;
    }
    Swal.close();
  }
}
